import type { LeaveType } from '@/features/leaveTypes/types/leaveType.types';
import {
  APPROVAL_STATUS_LABELS,
  DAY_UNIT,
  LEAVE_REQUEST_TYPE_LABELS,
} from '../constants/leaveApplication.constants';
import type { LeaveUsage } from '../types/leaveApplication.types';

const REQUEST_UNIT = 'request';
const OTHER_LEAVE_TYPE_CODE = 'OTHER';

export interface LeaveUsageCount {
  key: string;
  label: string;
  unit: string;
  getCount: (usage: LeaveUsage) => number;
}

interface ShownLeaveType {
  id: number;
  name: string;
  code: string;
}

function getLeaveTypeDays(usage: LeaveUsage, leaveTypeId: number): number {
  return usage.byType.find((bucket) => bucket.leaveTypeId === leaveTypeId)?.days ?? 0;
}

function putOtherLeaveTypeLast(leaveTypes: ShownLeaveType[]): ShownLeaveType[] {
  return [
    ...leaveTypes.filter((leaveType) => leaveType.code !== OTHER_LEAVE_TYPE_CODE),
    ...leaveTypes.filter((leaveType) => leaveType.code === OTHER_LEAVE_TYPE_CODE),
  ];
}

function getShownLeaveTypes(usages: LeaveUsage[], leaveTypes: LeaveType[]): ShownLeaveType[] {
  const usedBuckets = usages.flatMap((usage) => usage.byType);
  const usedLeaveTypeIds = new Set(usedBuckets.map((bucket) => bucket.leaveTypeId));
  const listedLeaveTypeIds = new Set(leaveTypes.map((leaveType) => leaveType.id));

  const listedTypes = leaveTypes
    .filter((leaveType) => leaveType.isActive || usedLeaveTypeIds.has(leaveType.id))
    .map((leaveType) => ({ id: leaveType.id, name: leaveType.name, code: leaveType.code }));
  const unlistedUsedTypes = [
    ...new Map(
      usedBuckets
        .filter((bucket) => !listedLeaveTypeIds.has(bucket.leaveTypeId))
        .map((bucket) => [
          bucket.leaveTypeId,
          { id: bucket.leaveTypeId, name: bucket.name, code: bucket.code },
        ]),
    ).values(),
  ];

  return putOtherLeaveTypeLast([...listedTypes, ...unlistedUsedTypes]);
}

export function getLeaveUsageCounts(
  usages: LeaveUsage[],
  leaveTypes: LeaveType[],
): LeaveUsageCount[] {
  const leaveTypeCounts = getShownLeaveTypes(usages, leaveTypes).map((leaveType) => ({
    key: `leave-type-${leaveType.id}`,
    label: leaveType.name,
    unit: DAY_UNIT,
    getCount: (usage: LeaveUsage) => getLeaveTypeDays(usage, leaveType.id),
  }));

  return [
    {
      key: 'attendance-claim',
      label: LEAVE_REQUEST_TYPE_LABELS.ATTENDANCE_CLAIM,
      unit: DAY_UNIT,
      getCount: (usage) => usage.attendanceClaimDays,
    },
    ...leaveTypeCounts,
    {
      key: 'rejected',
      label: APPROVAL_STATUS_LABELS.REJECTED,
      unit: REQUEST_UNIT,
      getCount: (usage) => usage.rejectedRequests,
    },
  ];
}
